import { useTranslation } from 'react-i18next';
import { RENOWN_ABLE } from '@pk/shared';
import type { CharacterView, HouseView, PerkChoiceView, PerkView } from '@pk/shared';
import { actorOf, useCommand } from '../api/hooks';
import { Bar, Btn, Inline, Tag } from './ui';

/** Renown, its tier and the bar to the next tier. Shown for every adult with any renown. */
export function RenownBlock({ c }: { c: CharacterView }) {
  const { t } = useTranslation();
  const renown = c.renown ?? 0;
  if (c.renown === undefined) return null;
  const next = c.renownNext ?? null;
  const tier = c.renownTier ?? 'none';
  const floor = tier === 'none' ? 0 : tier === 'able' ? RENOWN_ABLE : tier === 'renowned' ? 150 : tier === 'great' ? 400 : 1000;
  return (
    <div className="renown-block">
      <div className="kv">
        <span>{t('renown.title')}</span>
        <span className="num">{renown} {tier !== 'none' && <Tag tone="gold">{t(`renown.tier.${c.dubbed && (tier === 'renowned' || tier === 'great') ? `${tier}Knight` : tier}`)}</Tag>}</span>
      </div>
      {next !== null
        ? <Bar label={t('renown.toNext', { next })} value={Math.max(0, renown - floor)} max={Math.max(1, next - floor)} tone="gold" />
        : <p className="small muted">{t('renown.maxTier')}</p>}
    </div>
  );
}

function PerkCard({ p, onPick, busy }: { p: PerkView; onPick?: () => void; busy?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="perk-card">
      <div className="perk-head"><b>{p.name}</b><span className="muted small">{t(`attr.${p.skill}`)} · {t('perks.rank', { rank: p.tier })}</span></div>
      <p className="small">{p.description}</p>
      {onPick && <Btn small busy={busy} onClick={onPick}>{t('perks.choose')}</Btn>}
    </div>
  );
}

/** Pending perk pickers (own characters) and the list of chosen perks. */
export function PerksBlock({ c, house }: { c: CharacterView; house?: HouseView }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const pending: PerkChoiceView[] = c.pendingPerks ?? [];
  const chosen = c.perks ?? [];
  if (pending.length === 0 && chosen.length === 0 && !c.skills) return null;
  const pick = async (p: PerkChoiceView, perk: string) => {
    if (!house) return;
    await cmd.run({ type: 'choose_perk', actorId: actorOf(house), charId: c.id, skill: p.skill, tier: p.tier, perk });
  };
  return (
    <div className="perks-block">
      {house && pending.map((p) => (
        <div key={`${p.skill}-${p.tier}`} className="perk-pick">
          <div className="perk-pick-title"><b>{t('perks.pickTitle', { skill: t(`attr.${p.skill}`), rank: p.tier })}</b><span className="muted small">{t('perks.pickHint')}</span></div>
          <div className="perk-cards">
            {p.options.map((o) => <PerkCard key={o.key} p={o} busy={cmd.pending} onPick={() => void pick(p, o.key)} />)}
          </div>
        </div>
      ))}
      <Inline error={cmd.error} />
      {chosen.length > 0
        ? <div className="perk-cards chosen">{chosen.map((p) => <PerkCard key={p.key} p={p} />)}</div>
        : <p className="small muted">{t('perks.none')}</p>}
    </div>
  );
}
